"use client";

import { useRef, type MouseEvent } from "react";
import { AlertCircle, Check, Loader2, RotateCw, ShoppingBag } from "lucide-react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";

import type { PendingCartEntry } from "./pending-cart-lines";

interface PendingCartLineItemProps {
	line: PendingCartEntry;
	/** Replays the very same add — a full attempt cycle with a fresh attempt id. */
	onRetry: () => void;
}

/**
 * Transient cart row. Bridges click → authoritative server props so the drawer never
 * flashes its empty state, and turns into an inline error with a retry button when
 * the add failed. Deliberately without prices: nothing is confirmed yet, and the
 * money sections stay hidden until the real line arrives.
 *
 * Once the chain has failed, the retry button stays MOUNTED (disabled while the next
 * attempt runs) instead of being unmounted and re-created. That is what makes the
 * double-click defence below work: a replaced node resets the browser's own multi-click
 * counter, so the second click of one gesture would look like a brand new intent.
 */
export function PendingCartLineItem({ line, onRetry }: PendingCartLineItemProps) {
	const t = useTranslations("cart");
	const tProduct = useTranslations("product");
	const failed = line.status === "failed";
	const detail = line.dosageLabel ?? line.variantName;
	/**
	 * Synchronous lock for the click currently being handled: React re-renders only after
	 * the handler returned, so a second dispatch in the same tick would still meet an
	 * enabled button.
	 */
	const retryInFlightRef = useRef(false);

	const handleRetry = (event: MouseEvent<HTMLButtonElement>) => {
		// `detail` is the browser's own click counter for one pointer gesture — it uses the
		// OS double-click interval, so this is NOT a wait time of ours. A double-click is
		// ONE intent: only its first click may start a retry. Keyboard activation reports 0
		// and passes.
		if (event.detail > 1) return;
		if (retryInFlightRef.current || !failed) return;
		retryInFlightRef.current = true;
		try {
			onRetry();
		} finally {
			// The row re-renders into `pending` from here on; the ref only has to cover the
			// synchronous window in which the button is still enabled.
			retryInFlightRef.current = false;
		}
	};

	return (
		<li
			className="px-5 py-3 motion-safe:animate-in motion-safe:fade-in"
			data-testid="pending-cart-line"
			data-status={line.status}
			// Only a row whose outcome is still unknown is "busy". A Saleor-confirmed row is
			// done as far as the visitor is concerned; it lingers only until the server props
			// catch up, and announcing it as busy makes a screen reader wait on our re-render.
			aria-busy={line.status === "pending"}
		>
			<div className="flex gap-3">
				<div
					className={cn(
						"flex h-16 w-12 shrink-0 items-center justify-center rounded-md bg-secondary",
						failed ? "text-destructive" : "text-muted-foreground",
					)}
				>
					{failed ? (
						<AlertCircle className="h-4 w-4" aria-hidden />
					) : (
						<ShoppingBag className="h-4 w-4" aria-hidden />
					)}
				</div>

				<div className="min-w-0 flex-1">
					<p className="line-clamp-2 text-sm font-medium leading-tight">{line.productName}</p>
					{detail ? <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p> : null}

					<div className="mt-2 flex items-center justify-between gap-2">
						{failed ? (
							<span className="text-xs text-destructive">{t("addFailed")}</span>
						) : line.status === "confirmed" ? (
							// Saleor has confirmed this line. Saying "adding..." here is simply
							// untrue — the only thing still outstanding is our own re-render.
							<span
								className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
								aria-live="polite"
							>
								<Check className="h-3 w-3 text-success" aria-hidden />
								{tProduct("addedToBag")}
							</span>
						) : (
							<span
								className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
								aria-live="polite"
							>
								<Loader2 className="h-3 w-3 animate-spin" aria-hidden />
								{tProduct("adding")}
							</span>
						)}

						<span className="flex shrink-0 items-center gap-2">
							{line.hasFailed ? (
								<button
									type="button"
									onClick={handleRetry}
									disabled={!failed}
									className="inline-flex min-h-11 items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
								>
									<RotateCw className="h-3 w-3" aria-hidden />
									{t("retryAdd")}
								</button>
							) : null}
							<span className="rounded border border-border px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
								{line.quantity}
							</span>
						</span>
					</div>
				</div>
			</div>
		</li>
	);
}
